import { useEffect, useState } from 'react'
import { Check, Copy, Mail } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SwitchField } from '@/components/ui/controls'
import { Field, Label, Textarea } from '@/components/ui/field'
import {
  KEYTERM_LIMITS,
  RECORDING_POLICIES,
  VIDEO_MODES,
  keytermError,
  parseKeyterms,
  type OrgSettings,
} from '@/data/settings'
import { Consequence, SettingsCard, SwitchGroup } from './SettingsCard'
import { ImageWell } from './ImageWell'

/**
 * What gets recorded, how it is written down, and what the recorder does once
 * it is in the room — in the order the questions arise.
 */
export function RecordingSettings({
  draft,
  onChange,
}: {
  draft: OrgSettings
  onChange: (next: OrgSettings) => void
}) {
  const { recording, bot } = draft

  const setRecording = (patch: Partial<OrgSettings['recording']>) =>
    onChange({ ...draft, recording: { ...recording, ...patch } })
  const setBot = (patch: Partial<OrgSettings['bot']>) =>
    onChange({ ...draft, bot: { ...bot, ...patch } })

  const policy = RECORDING_POLICIES.find((option) => option.value === recording.defaultPolicy)!
  const video = VIDEO_MODES.find((option) => option.value === recording.videoMode)!

  return (
    <>
      <SettingsCard title="Automatic recording" meta="a meeting choice always wins">
        <Field label="Default">
          {(props) => (
            <Select
              id={props.id}
              value={recording.defaultPolicy}
              onValueChange={(value) => setRecording({ defaultPolicy: value })}
              options={RECORDING_POLICIES}
              className="sm:max-w-sm"
            />
          )}
        </Field>

        {/* The rule, stated as what is true now rather than as three options
            the reader has to re-derive from the label they just picked. */}
        <Consequence>
          {policy.description} A choice made on a single meeting, or on a series,
          overrides this for that meeting.
        </Consequence>

        {/* Under `all` there is nothing left for an invite to opt in, so the
            address is a fact with no consequence here. */}
        {recording.defaultPolicy === 'all' ? null : (
          <RecorderInviteAddress
            email={recording.recorderInviteEmail}
            aliases={recording.recorderInviteAliases}
          />
        )}
      </SettingsCard>

      {/* Its own card rather than a row in the one above: that card decides
          WHICH meetings are recorded, this decides what is kept from one.

          Not a switch for shared screens. The recorder captures a single stage
          and a share arrives as part of it, so there is no version of this that
          keeps the speakers and drops the slide — only versions that keep
          less. Three named outcomes say that; a switch would have implied a
          fourth that does not exist. */}
      <SettingsCard title="Video" meta="shares become their own chapters">
        <Field label="Keep">
          {(props) => (
            <Select
              id={props.id}
              value={recording.videoMode}
              onValueChange={(value) => setRecording({ videoMode: value })}
              options={VIDEO_MODES}
              className="sm:max-w-sm"
            />
          )}
        </Field>

        <Consequence>{video.description}</Consequence>
      </SettingsCard>

      {/* After the two cards about what is captured, because it is only worth
          asking once there is a recording to get right. */}
      <Keyterms
        terms={draft.transcription.keyterms}
        onChange={(keyterms) => onChange({ ...draft, transcription: { keyterms } })}
      />

      {/* No bot name. The recorder signs in as a real account from the tenant's
          pool, so the roster shows that account's directory name — a name field
          here would be a setting that changes nothing. */}
      <SettingsCard title="Meeting bot" meta="what the room is told">
        <SwitchGroup>
          <SwitchField
            label="Post a message when it joins"
            hint="Off means it joins silently. Participants still see it in the roster."
            checked={bot.announce}
            onCheckedChange={(value) => setBot({ announce: value })}
          />
        </SwitchGroup>

        {bot.announce ? (
          <Field
            label="Message"
            hint="Sent once, to the meeting chat, as the recorder joins."
          >
            {(props) => (
              <Textarea
                {...props}
                rows={5}
                value={bot.message}
                maxLength={600}
                placeholder="Describe what is being recorded and who will be able to read it."
                className="leading-relaxed"
                onChange={(event) => setBot({ message: event.target.value })}
              />
            )}
          </Field>
        ) : (
          <Consequence>
            Nothing is posted to the meeting chat. Where a jurisdiction requires
            the room to be told a recording is running, that notice has to come
            from the organiser instead.
          </Consequence>
        )}
      </SettingsCard>

      <SettingsCard title="Bot background" meta="its video feed, 16:9">
        <div className="grid gap-4 sm:grid-cols-2">
          <ImageWell
            label="Recording"
            hint="Shown for the whole time the recorder is capturing."
            value={bot.recordingImage}
            onChange={(value) => setBot({ recordingImage: value })}
          />
          <ImageWell
            label="Not recording"
            hint={
              bot.pausedImage
                ? 'Shown while the recording is paused.'
                : 'Optional. The recording image is used while paused when this is empty.'
            }
            value={bot.pausedImage}
            onChange={(value) => setBot({ pausedImage: value })}
          />
        </div>
      </SettingsCard>
    </>
  )
}

/**
 * The org-wide keyterm list.
 *
 * One per line rather than chips: the list is pasted from a glossary as often
 * as it is typed, and a chip editor turns a paste of forty terms into forty
 * keystrokes. The raw text is local state so a half-typed line survives, while
 * the draft only ever holds the normalised list — the same normalisation the
 * server does, so the count below the field is the count that gets saved.
 */
function Keyterms({
  terms,
  onChange,
}: {
  terms: string[]
  onChange: (terms: string[]) => void
}) {
  const [text, setText] = useState(() => terms.join('\n'))

  // Follows the draft when it changes from outside — a discard, or a save that
  // came back normalised. While typing, the parse already equals `terms`, so
  // this does not fight the cursor.
  useEffect(() => {
    setText((current) =>
      parseKeyterms(current).join('\n') === terms.join('\n') ? current : terms.join('\n')
    )
  }, [terms])

  const rejected = terms
    .map((term) => ({ term, reason: keytermError(term) }))
    .filter((entry): entry is { term: string; reason: string } => entry.reason !== null)

  const overCount = terms.length > KEYTERM_LIMITS.maxTerms
  const error = overCount
    ? `${terms.length} terms — the limit is ${KEYTERM_LIMITS.maxTerms}.`
    : rejected.length > 0
      ? rejected.map((entry) => `“${entry.term}” is ${entry.reason}`).join('; ')
      : null

  return (
    <SettingsCard
      title="Transcription keyterms"
      meta={`${terms.length} of ${KEYTERM_LIMITS.maxTerms}`}
    >
      <Field
        label="Words the transcriber gets wrong"
        hint="One per line. Company and product names, acronyms, deal names, industry jargon."
        error={error}
      >
        {(props) => (
          <Textarea
            {...props}
            rows={8}
            spellCheck={false}
            value={text}
            placeholder={'Atlas Carve-out\nEBITDA\nProject Northwind'}
            className="resize-y font-mono text-xs sm:max-w-md"
            onChange={(event) => {
              setText(event.target.value)
              onChange(parseKeyterms(event.target.value))
            }}
          />
        )}
      </Field>

      <Consequence>
        {terms.length === 0
          ? 'Nothing is biased. Names the model has not seen come out as the nearest ordinary word, and stay wrong in the notes and in search.'
          : 'Applied to every recording, on top of each space’s own list and the names on the invite. A keyterm raises the odds of a spelling — it does not force one, and it cannot invent a word that was never said.'}
      </Consequence>
    </SettingsCard>
  )
}

/**
 * The recorder's mailbox — shown, not edited.
 *
 * It is provisioned when the tenant is set up, and the address is the mailbox:
 * changing the string here would not move any mail, it would only stop this
 * page from describing what actually happens. So it reads as a fact, with the
 * one thing an administrator can do about it (copy it, and ask us) beside it.
 */
function RecorderInviteAddress({
  email,
  aliases,
}: {
  email: string
  aliases: readonly string[]
}) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    await navigator.clipboard.writeText(email)
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label>Recording invite address</Label>

      <div className="flex items-center gap-2 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface-sunken)] px-3 py-2 sm:max-w-sm">
        <Mail className="size-3.5 shrink-0 text-[var(--color-fg-subtle)]" />
        <span className="min-w-0 flex-1 truncate font-mono text-xs text-[var(--color-fg)]">
          {email}
        </span>
        <Button size="iconSm" variant="ghost" aria-label="Copy address" onClick={() => void copy()}>
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
        </Button>
      </div>

      <p className="text-xs text-[var(--color-fg-subtle)]">
        Inviting this address to a meeting records it, whatever the default says.
        {aliases.length > 0 ? ` Also accepted: ${aliases.join(', ')}.` : ''}
      </p>

      <Consequence>
        The mailbox is created when the organisation is set up and cannot be
        changed from here. To use a different address, contact nohup — we move
        the mailbox and keep the old address working.
      </Consequence>
    </div>
  )
}
